/**
 * Jalons et gemmes — logique pure (§6).
 *
 * Les gemmes ne sont **jamais** produites passivement : elles ne s'obtiennent que par
 * jalons. C'est ce qui les garde rares, donc désirables, et ce qui interdit d'en faire
 * une seconde monnaie de production déguisée.
 *
 * Chaque jalon a un identifiant **stable**, écrit dans la sauvegarde. Le renommer
 * re-créditerait le jalon à tous les joueurs existants — d'où des identifiants
 * construits, jamais des libellés.
 */

import { MILESTONE_REWARDS } from '@/config/balance';
import { BUILDINGS } from '@/config/buildings';

export type MilestoneKind =
  | 'first-building'
  | 'land'
  | 'visitors'
  | 'production'
  | 'prestige'
  /* ROADMAP 3.2 — trois familles de plus, mêmes règles : générées, identifiants stables. */
  | 'roads'
  | 'decor'
  | 'eras';

export interface Milestone {
  /** Identifiant stable, persisté. Ne jamais le modifier. */
  readonly id: string;
  readonly kind: MilestoneKind;
  readonly gems: number;
  /** Valeur du seuil, pour afficher « plus que N avant… ». */
  readonly threshold: number;
  /** Clé i18n du libellé, résolue au moment de l'affichage — `core` ne connaît aucun texte. */
  readonly labelKey: string;
  /** Paramètre d'interpolation du libellé. */
  readonly labelParam?: string | number;
}

/** Progression observable par les jalons. */
export interface MilestoneProgress {
  readonly buildingCounts: Readonly<Record<string, number>>;
  readonly landPurchases: number;
  readonly visitorsReceived: number;
  /** Pièces cumulées depuis toujours, prestiges compris. */
  readonly totalProduced: number;
  readonly prestigeLevel: number;
  /** Cases de rue du réseau (ROADMAP 3.2). */
  readonly roadCells: number;
  /** Décorations posées — traces de visiteurs et objets payés confondus. */
  readonly decorations: number;
}

/** Catalogue complet, construit une fois. */
export const MILESTONES: readonly Milestone[] = [
  ...BUILDINGS.map((definition): Milestone => ({
    id: `first-building:${definition.type}`,
    kind: 'first-building',
    gems: MILESTONE_REWARDS.firstOfType,
    threshold: 1,
    labelKey: 'milestones.firstBuilding',
    labelParam: definition.type,
  })),
  ...Array.from({ length: 12 }, (_, index): Milestone => ({
    id: `land:${index + 1}`,
    kind: 'land',
    gems: MILESTONE_REWARDS.landExpansion,
    threshold: index + 1,
    labelKey: 'milestones.land',
    labelParam: index + 1,
  })),
  ...MILESTONE_REWARDS.visitorTiers.map((tier, index): Milestone => ({
    // Identifiant indexé et non chiffré : `String(1e21)` rend « 1e+21 », une chaîne
    // qui dépend du formatage des nombres du moteur JS. Un identifiant persisté ne
    // doit dépendre que de sa position dans l'échelle.
    id: `visitors:t${String(index)}`,
    kind: 'visitors',
    gems: MILESTONE_REWARDS.visitorTierReward,
    threshold: tier,
    labelKey: 'milestones.visitors',
    labelParam: tier,
  })),
  ...MILESTONE_REWARDS.productionTiers.map((tier, index): Milestone => ({
    id: `production:t${String(index)}`,
    kind: 'production',
    gems: MILESTONE_REWARDS.productionTierReward,
    threshold: tier,
    labelKey: 'milestones.production',
    labelParam: tier,
  })),
  {
    id: 'prestige:1',
    kind: 'prestige',
    gems: MILESTONE_REWARDS.firstPrestige,
    threshold: 1,
    labelKey: 'milestones.firstPrestige',
  },
  ...MILESTONE_REWARDS.roadTiers.map((tier, index): Milestone => ({
    id: `roads:t${String(index)}`,
    kind: 'roads',
    gems: MILESTONE_REWARDS.roadTierReward,
    threshold: tier,
    labelKey: 'milestones.roads',
    labelParam: tier,
  })),
  ...MILESTONE_REWARDS.decorTiers.map((tier, index): Milestone => ({
    id: `decor:t${String(index)}`,
    kind: 'decor',
    gems: MILESTONE_REWARDS.decorTierReward,
    threshold: tier,
    labelKey: 'milestones.decor',
    labelParam: tier,
  })),
  ...MILESTONE_REWARDS.eraTiers.map((tier, index): Milestone => ({
    id: `eras:t${String(index)}`,
    kind: 'eras',
    gems: MILESTONE_REWARDS.eraTierReward,
    threshold: tier,
    labelKey: 'milestones.eras',
    labelParam: tier,
  })),
];

const BY_ID = new Map(MILESTONES.map((milestone) => [milestone.id, milestone]));

export function milestoneById(id: string): Milestone | undefined {
  return BY_ID.get(id);
}

/** Valeur atteinte par le joueur pour le type de jalon donné. */
function currentValue(milestone: Milestone, progress: MilestoneProgress): number {
  switch (milestone.kind) {
    case 'first-building': {
      const type = milestone.id.slice('first-building:'.length);
      return progress.buildingCounts[type] ?? 0;
    }
    case 'land':
      return progress.landPurchases;
    case 'visitors':
      return progress.visitorsReceived;
    case 'production':
      return progress.totalProduced;
    case 'prestige':
      return progress.prestigeLevel;
    case 'roads':
      return progress.roadCells;
    case 'decor':
      return progress.decorations;
    /*
      Les ères se comptent au MÊME compteur que le premier prestige : \n      vaut 1 après la première. Un second compteur dériverait à la première migration,
      et il n'y a rien à compter d'autre — le niveau EST le nombre d'ères franchies.
    */
    case 'eras':
      return progress.prestigeLevel;
  }
}

export function isReached(milestone: Milestone, progress: MilestoneProgress): boolean {
  return currentValue(milestone, progress) >= milestone.threshold;
}

export interface MilestoneClaim {
  readonly milestones: readonly Milestone[];
  readonly gems: number;
}

/**
 * Jalons atteints mais pas encore crédités.
 *
 * Rendus dans l'ordre du catalogue, sans muter l'ensemble reçu : à l'appelant
 * d'appliquer. Cette séparation permet de déclencher le juice (E§1.4) au bon moment
 * plutôt qu'au milieu d'un calcul.
 */
export function claimableMilestones(
  progress: MilestoneProgress,
  alreadyClaimed: ReadonlySet<string>,
): MilestoneClaim {
  const milestones: Milestone[] = [];
  let gems = 0;

  for (const milestone of MILESTONES) {
    if (alreadyClaimed.has(milestone.id)) continue;
    if (!isReached(milestone, progress)) continue;

    milestones.push(milestone);
    gems += milestone.gems;
  }

  return { milestones, gems };
}

export interface NextMilestone {
  readonly milestone: Milestone;
  readonly current: number;
  readonly remaining: number;
  /** Progression de 0 à 1, pour la barre toujours visible (E§1.9). */
  readonly ratio: number;
}

/**
 * Prochain jalon d'un type donné, avec sa progression.
 *
 * Le panneau « Visites » affiche « plus que 12 visiteurs avant la fontaine » (§16.1) :
 * un objectif chiffré et proche vaut mieux qu'une liste de récompenses lointaines.
 */
export function nextMilestoneOfKind(
  kind: MilestoneKind,
  progress: MilestoneProgress,
  alreadyClaimed: ReadonlySet<string>,
): NextMilestone | null {
  const candidates = MILESTONES.filter(
    (milestone) => milestone.kind === kind && !alreadyClaimed.has(milestone.id),
  ).sort((a, b) => a.threshold - b.threshold);

  for (const milestone of candidates) {
    const current = currentValue(milestone, progress);
    if (current >= milestone.threshold) continue;

    return {
      milestone,
      current,
      remaining: milestone.threshold - current,
      ratio: milestone.threshold <= 0 ? 1 : Math.min(1, current / milestone.threshold),
    };
  }

  return null;
}
